import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, Info } from "@phosphor-icons/react";
import api from "@/lib/api";

const FAQ_LINK = "/faqs/what-happens-when-my-import-finishes";

export function ImportNextSteps({ user }) {
  return (
    <aside className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="import-next-steps">
      <p className="flex items-center gap-2 font-display font-semibold text-slate-800 mb-3">
        <Info size={18} className="text-rf-orange" /> What happens next
      </p>
      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-600">
        <li>We read your recipes and list every one we find — app files take seconds, lists of links can take a few minutes.</li>
        <li>Untick any you don&apos;t want, then tap <b>Save</b>{user ? "" : " (you'll sign in or sign up first)"}.</li>
        <li>They go into a <b>new private cookbook</b> — named whatever you type, or <b>&ldquo;Imported from [app] · [month]&rdquo;</b> — or{user ? "" : ", once signed in,"} into one of <b>your existing cookbooks</b> if you pick it from the list. You&apos;re taken straight to it.</li>
        <li>
          <EnvelopeSimple size={14} className="inline text-rf-orange mr-1" />
          Links or bookmarks taking over a minute? Close the tab —{" "}
          {user ? "we'll save them for you and email you a link to the cookbook." : "leave your email while it works and we'll send you a link back."}
        </li>
      </ol>
      <Link to={FAQ_LINK} className="inline-block mt-3 text-sm font-semibold text-rf-orange underline" data-testid="import-next-steps-faq">
        Read the full guide →
      </Link>
    </aside>
  );
}

export function ImportDestination({ name, count, user, existing = false }) {
  return (
    <p className="px-5 py-3 bg-amber-50/70 border-b border-amber-200 text-sm text-slate-700" data-testid="import-destination">
      {user ? "Tapping Save puts" : "After you sign in, Save puts"} the {count} ticked recipe{count === 1 ? "" : "s"} into {existing ? "your existing cookbook" : "a new private cookbook called"}{" "}
      <b data-testid="import-destination-name">&ldquo;{name}&rdquo;</b>{existing ? " (any already in it are skipped; imported recipes stay private)" : ""}. You&apos;ll be taken straight to it, and it stays in <b>My Cookbooks</b>.
    </p>
  );
}

export function ImportWaitNote({ jobId, user, cookbookName }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) {
    return (
      <p className="mt-3 text-sm text-slate-600 flex items-start gap-2" data-testid="import-wait-note">
        <EnvelopeSimple size={16} className="text-rf-orange shrink-0 mt-0.5" />
        <span>
          Taking a while? You can close this tab. If it runs for more than a minute we&apos;ll save everything into a new cookbook called{" "}
          <b>&ldquo;{cookbookName}&rdquo;</b> and email you a link to it. (Over your free-plan limit? The email lets you choose which to keep.)
        </span>
      </p>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/import/${jobId}/notify`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your email");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <p className="mt-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="import-notify-confirm">
        <CheckCircle size={16} weight="fill" /> We&apos;ll email {email} a link back to your recipes when they&apos;re ready. You can close this tab.
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="mt-3 flex flex-wrap items-center gap-2" data-testid="import-notify-form">
      <span className="text-sm text-slate-600">Taking a while? We&apos;ll email you a link back to your recipes:</span>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
             data-testid="import-notify-email" className="border border-slate-300 rounded-full px-3 py-1.5 text-sm" />
      <button type="submit" disabled={busy} data-testid="import-notify-submit"
              className="px-4 py-1.5 rounded-full bg-rf-orange text-white text-sm font-semibold disabled:opacity-50">
        {busy ? "Saving…" : "Email me"}
      </button>
    </form>
  );
}

export function ImportSavedNote({ cookbookId, cookbookTitle }) {
  return (
    <div className="p-5 bg-emerald-50 border-b border-emerald-200 flex flex-wrap items-center gap-3" data-testid="import-saved-note">
      <CheckCircle size={22} weight="fill" className="text-emerald-600" />
      <p className="text-sm text-emerald-900 flex-1">
        Saved into your cookbook {cookbookTitle ? <b>&ldquo;{cookbookTitle}&rdquo;</b> : null} — it&apos;s in <b>My Cookbooks</b>.
      </p>
      {cookbookId && (
        <Link to={`/cb/${cookbookId}`} data-testid="import-saved-open" className="text-sm font-semibold text-emerald-800 underline">Open cookbook →</Link>
      )}
    </div>
  );
}
